"use client";

import { useTranslations } from "next-intl";
import { Logo } from "@/components/logo";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { CaretDownIcon, TranslateIcon } from "@phosphor-icons/react/ssr";
import { locales, type Locale } from "@/lib/locales";
import { useLocaleContext } from "@/contexts/LocaleContext";
import { cn } from "@/lib/utils";

interface AuthHeaderProps {
    heading?: string;
    subheading?: string;
}

export function AuthHeader({ heading, subheading }: AuthHeaderProps) {
    const t = useTranslations();
    const { locale, setLocale, isLocaleUpdating } = useLocaleContext();

    const switchLocale = (newLocale: Locale) => {
        setLocale(newLocale);
    };

    const getLanguageLabel = (loc: string) => {
        return loc === "en" ? t("language.english") : t("language.norwegian");
    };

    const getLanguageShortLabel = (loc: string) => {
        return loc === "en" ? "EN" : "NO";
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-x-2">
                    <Logo />
                </div>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={isLocaleUpdating}
                            className="gap-1.5"
                        >
                            <TranslateIcon size={16} />
                            <span className="text-sm font-medium">
                                {getLanguageShortLabel(locale)}
                            </span>
                            <CaretDownIcon size={12} />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {locales.map((availableLocale) => (
                            <DropdownMenuItem
                                key={availableLocale}
                                disabled={isLocaleUpdating}
                                onClick={() =>
                                    switchLocale(availableLocale as Locale)
                                }
                                className={cn(
                                    locale === availableLocale &&
                                        "bg-blue-100 focus:bg-blue-100 dark:bg-blue-900 dark:focus:bg-blue-900"
                                )}
                            >
                                {getLanguageLabel(availableLocale)}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            {(heading || subheading) && (
                <div>
                    {heading && (
                        <h1 className="text-2xl font-semibold text-foreground sm:text-xl/8">
                            {heading}
                        </h1>
                    )}
                    {subheading && (
                        <p className="mt-1 text-sm text-muted-foreground">
                            {subheading}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
